import React, { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useRecoilValue } from 'recoil';
import polyline from '@mapbox/polyline';
import { atom_borders } from '@app/shared/state/tour.atom';

interface TourBoundariesProps {
  color?: string;
  fillColor?: string;
  fillOpacity?: number;
  weight?: number;
  opacity?: number;
  interactive?: boolean;
  minZoom?: number;
  shadow?: boolean;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  /** When true, colors everything OUTSIDE the boundary (mask mode) */
  maskMode?: boolean;
}

// World bounds for mask mode (covers entire map)
const WORLD_BOUNDS: [number, number][] = [
  [-90, -180],
  [-90, 180],
  [90, 180],
  [90, -180],
];

export const TourBoundaries = React.memo(function TourBoundaries({
  color: colorProp = 'transparent',
  fillColor: fillColorProp,
  fillOpacity: fillOpacityProp,
  weight = 3,
  opacity = 0.8,
  minZoom = 8,
  shadow = false,
  shadowColor = 'rgba(0, 0, 0, 0.3)',
  shadowBlur = 8,
  maskMode = true,
}: TourBoundariesProps) {
  const borders = useRecoilValue(atom_borders);
  const map = useMap();

  const fillColor = fillColorProp ?? (maskMode ? 'rgba(80, 80, 80, 0.7)' : 'transparent');
  const fillOpacity = fillOpacityProp ?? (maskMode ? 1 : 0.15);

  // Decode borders from Firebase. Reference stable as long as `borders` doesn't change.
  const decodedBorders = useMemo(() => {
    if (!borders || borders.length === 0) return [];
    return borders.map((border, index) => {
      try {
        const decoded = polyline.decode(border.cordinate) as [number, number][];
        return {
          coords: decoded,
          color: border.color.startsWith('#') ? border.color : `#${border.color}`,
          maskMode: border.maskMode ?? true,
        };
      } catch (e) {
        console.warn(`[TourBoundaries] Failed to decode polyline at index ${index}:`, e);
        return null;
      }
    }).filter(Boolean) as { coords: [number, number][]; color: string; maskMode: boolean }[];
  }, [borders]);

  // Memoized so the layer-setup effect doesn't tear down on every render.
  const maskPositions = useMemo(
    () => (maskMode ? [WORLD_BOUNDS, ...decodedBorders.map(b => b.coords)] : null),
    [maskMode, decodedBorders],
  );

  const layersRef = useRef<L.Polygon[]>([]);

  useEffect(() => {
    if (decodedBorders.length === 0) return;

    // Pane setup
    let pane = map.getPane('regional-boundary');
    if (!pane) {
      map.createPane('regional-boundary');
      pane = map.getPane('regional-boundary')!;
    }
    pane.style.zIndex = '401';
    pane.style.pointerEvents = 'none';
    pane.style.willChange = 'opacity';
    pane.style.transition = 'opacity 60ms linear';

    // Build polygons once
    const paneOpts = { pane: 'regional-boundary' };
    if (maskMode && maskPositions) {
      const mask = L.polygon(maskPositions, {
        ...paneOpts,
        color: 'transparent',
        fillColor,
        fillOpacity,
        weight: 0,
        opacity,
        interactive: false,
      }).addTo(map);
      layersRef.current.push(mask);

      decodedBorders.forEach(border => {
        const line = L.polygon(border.coords, {
          ...paneOpts,
          color: colorProp !== 'transparent' ? colorProp : border.color,
          fillColor: 'transparent',
          fillOpacity: 0,
          weight,
          opacity,
          interactive: false,
        }).addTo(map);
        layersRef.current.push(line);
      });
    } else {
      if (shadow) {
        decodedBorders.forEach(border => {
          const s = L.polygon(border.coords, {
            ...paneOpts,
            color: shadowColor,
            fillColor: 'transparent',
            fillOpacity: 0,
            weight: weight + shadowBlur,
            opacity: 0.3,
            interactive: false,
          }).addTo(map);
          layersRef.current.push(s);
        });
      }
      decodedBorders.forEach(border => {
        const p = L.polygon(border.coords, {
          ...paneOpts,
          color: colorProp !== 'transparent' ? colorProp : border.color,
          fillColor,
          fillOpacity,
          weight,
          opacity,
          interactive: false,
        }).addTo(map);
        layersRef.current.push(p);
      });
    }

    // Visibility threshold — toggled via DOM, not React render.
    let belowThreshold = map.getZoom() < minZoom;
    pane.style.display = belowThreshold ? 'none' : '';

    const handleZoomEnd = () => {
      const belowNow = map.getZoom() < minZoom;
      const wasBelow = belowThreshold;
      belowThreshold = belowNow;
      pane!.style.display = belowNow ? 'none' : '';

      // Fade back in (covers the drift-during-zoom)
      pane!.style.transition = 'opacity 300ms ease-out';
      pane!.style.opacity = '1';

      // Pop-in animation when crossing hidden → visible
      if (wasBelow && !belowNow) {
        layersRef.current.forEach(layer => {
          const path = (layer as any)._path as SVGElement | undefined;
          if (!path) return;
          path.classList.remove('boundary-pop-in');
          // force reflow so the animation restarts cleanly
          void path.getBoundingClientRect();
          path.classList.add('boundary-pop-in');
          setTimeout(() => path.classList.remove('boundary-pop-in'), 500);
        });
      }
    };

    // Fade out at zoom start ONLY when the map is rotated (heading-up active).
    // At bearing=0 the boundary's drift during pinch-zoom is acceptable; we only hide
    // when rotation makes the misalignment visually obvious.
    const handleZoomStart = () => {
      const bearing = (map as any).getBearing ? (map as any).getBearing() : 0;
      if (Math.abs(bearing) < 0.5) return;
      pane!.style.transition = 'opacity 60ms linear';
      pane!.style.opacity = '0';
    };

    map.on('zoomstart', handleZoomStart);
    map.on('zoomend', handleZoomEnd);

    return () => {
      layersRef.current.forEach(l => map.removeLayer(l));
      layersRef.current = [];
      map.off('zoomstart', handleZoomStart);
      map.off('zoomend', handleZoomEnd);
    };
  }, [
    map,
    decodedBorders,
    maskPositions,
    maskMode,
    minZoom,
    fillColor,
    fillOpacity,
    weight,
    opacity,
    colorProp,
    shadow,
    shadowColor,
    shadowBlur,
  ]);

  return null;
});
